// SCR-STAFF-931 — اتاق‌ها (US-ORG-010، 012، 014): جدول اتاق‌ها با فیلتر شعبه، نوع و وضعیت؛ فرم (نوع، بخش، طبقه، برچسب‌ها، قابل
// رزرو)؛ غیرفعال‌سازی با نمایش تعداد نوبت‌های آینده و تأیید صریح (BR-ORG-014) و فعال‌سازی دوباره.
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type DepartmentView,
  type ResourceTagView,
  type RoomStatus,
  type RoomType,
  type RoomView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  EmptyState,
  ErrorState,
  faDigits,
  latinDigits,
  FormDialog,
  ListPage,
  LoadingState,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { useController, useForm, type Control } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  BranchFilter,
  ORG_CODE_PATTERN,
  orgKeys,
  ROOM_TYPES,
  useBranchSelection,
  useResourceTags,
  useTagLabel,
} from './common';
import { RoomUnitsDialog } from './RoomUnitsDialog';

export const SCR_STAFF_931 = 'SCR-STAFF-931';

export function RoomsPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.room.manage');
  const branches = useBranchSelection();
  const tags = useResourceTags();
  const tagLabel = useTagLabel();
  const [type, setType] = useState<RoomType | ''>('');
  const [status, setStatus] = useState<RoomStatus | ''>('');
  const [editing, setEditing] = useState<RoomView | null | undefined>(undefined);
  const [deactivating, setDeactivating] = useState<RoomView | null>(null);
  const [unitsOf, setUnitsOf] = useState<RoomView | null>(null);
  const queryClient = useQueryClient();

  const rooms = useQuery({
    queryKey: [...orgKeys.rooms(session?.tenantId), branches.branchId, type, status],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/rooms', {
          params: {
            query: { branchId: branches.branchId, type: type || undefined, status: status || undefined },
          },
        }),
      ),
    enabled: Boolean(branches.branchId),
  });
  const departments = useQuery({
    queryKey: orgKeys.departments(session?.tenantId, branches.branchId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/departments', {
          params: { query: { branchId: branches.branchId } },
        }),
      ),
    enabled: Boolean(branches.branchId),
  });
  const departmentName = (id: string | null | undefined) =>
    departments.data?.items.find((department) => department.id === id)?.name ?? t('common:states.none');
  const tagByCode = (code: string) => tags.data?.items.find((tag) => tag.code === code);

  const activate = useMutation({
    mutationFn: (room: RoomView) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/rooms/{id}:activate', {
          params: { path: { id: room.id } },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: orgKeys.rooms(session?.tenantId) }),
  });

  const columns: DataTableColumn<RoomView>[] = [
    { id: 'code', headerKey: 'org:rooms.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'name', headerKey: 'org:rooms.columns.name', cell: (row) => row.name },
    { id: 'type', headerKey: 'org:rooms.columns.type', cell: (row) => t(`org:room_type.${row.type}`) },
    {
      id: 'department',
      headerKey: 'org:rooms.columns.department',
      cell: (row) => departmentName(row.departmentId),
    },
    {
      id: 'floor',
      headerKey: 'org:rooms.columns.floor',
      cell: (row) => (row.floor ? faDigits(row.floor) : t('common:states.none')),
    },
    {
      id: 'tags',
      headerKey: 'org:rooms.columns.tags',
      cell: (row) =>
        row.tagCodes.length === 0 ? (
          t('common:states.none')
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.tagCodes.map((code) => (
              <Chip key={code} size="small" variant="outlined" label={tagLabel(tagByCode(code), code)} />
            ))}
          </Stack>
        ),
    },
    {
      id: 'bookable',
      headerKey: 'org:rooms.columns.bookable',
      cell: (row) =>
        Number(row.capacity) > 1
          ? `${row.isBookable ? t('org:rooms.bookable') : t('org:rooms.not_bookable')} · ${t('org:rooms.capacity', { n: faDigits(Number(row.capacity)) })}`
          : row.isBookable
            ? t('org:rooms.bookable')
            : t('org:rooms.not_bookable'),
    },
    {
      id: 'status',
      headerKey: 'org:rooms.columns.status',
      cell: (row) => (
        <span data-testid={`room-status-${row.code}`} data-status={row.status}>
          <StatusChip tone={row.status === 'Active' ? 'active' : 'inactive'} />
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'org:rooms.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('org:units.open')}>
            <span>
              <IconButton
                aria-label={`${t('org:units.open')} ${row.name}`}
                onClick={() => setUnitsOf(row)}
                data-testid={`room-units-${row.code}`}
              >
                <BedOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('org:rooms.edit')}>
            <span>
              <IconButton
                aria-label={`${t('org:rooms.edit')} ${row.name}`}
                onClick={() => setEditing(row)}
                disabled={!write.canWrite}
                data-testid={`room-edit-${row.code}`}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          {row.status === 'Active' ? (
            <Tooltip title={t('org:rooms.deactivate')}>
              <span>
                <IconButton
                  aria-label={`${t('org:rooms.deactivate')} ${row.name}`}
                  onClick={() => setDeactivating(row)}
                  disabled={!write.canWrite}
                  data-testid={`room-deactivate-${row.code}`}
                >
                  <BlockOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t('org:rooms.activate')}>
              <span>
                <IconButton
                  aria-label={`${t('org:rooms.activate')} ${row.name}`}
                  onClick={() => activate.mutate(row)}
                  disabled={!write.canWrite || activate.isPending}
                  data-testid={`room-activate-${row.code}`}
                >
                  <RestartAltOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_931}
      titleKey="org:rooms.title"
      subtitleKey="org:rooms.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditing(null)}
          disabled={!write.canWrite || !branches.branchId}
          data-testid="room-create"
        >
          {t('org:rooms.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <BranchFilter
              options={branches.options}
              value={branches.branchId}
              onChange={branches.setBranchId}
            />
            <TextField
              select
              size="small"
              label={t('org:rooms.filters.type')}
              value={type}
              onChange={(event) => setType(event.target.value as RoomType | '')}
              sx={{ minWidth: 180 }}
              slotProps={{ htmlInput: { 'data-testid': 'room-filter-type' } }}
            >
              <MenuItem value="">{t('org:rooms.filters.all')}</MenuItem>
              {ROOM_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`org:room_type.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('org:rooms.filters.status')}
              value={status}
              onChange={(event) => setStatus(event.target.value as RoomStatus | '')}
              sx={{ minWidth: 160 }}
              slotProps={{ htmlInput: { 'data-testid': 'room-filter-status' } }}
            >
              <MenuItem value="">{t('org:rooms.filters.all')}</MenuItem>
              <MenuItem value="Active">{t('common:status.active')}</MenuItem>
              <MenuItem value="Inactive">{t('common:status.inactive')}</MenuItem>
            </TextField>
          </Stack>
          <WriteNotice write={write} />
          {activate.error ? <ErrorState error={activate.error} compact /> : null}
        </Stack>
      }
    >
      {branches.options.length === 0 ? (
        <EmptyState titleKey="org:no_branch" />
      ) : (
        <DataTable
          captionKey="org:rooms.caption"
          columns={columns}
          rows={rooms.data?.items}
          getRowId={(row) => row.id}
          isLoading={rooms.isPending}
          error={rooms.error}
          onRetry={() => void rooms.refetch()}
          emptyTitleKey="org:rooms.empty"
        />
      )}
      {editing !== undefined && branches.branchId && (
        <RoomFormDialog
          room={editing}
          branchId={editing?.branchId ?? branches.branchId}
          branchName={branches.nameOf(editing?.branchId ?? branches.branchId) ?? ''}
          departments={departments.data?.items ?? []}
          tags={tags.data?.items ?? []}
          onClose={() => setEditing(undefined)}
        />
      )}
      {deactivating && <DeactivateRoomDialog room={deactivating} onClose={() => setDeactivating(null)} />}
      {unitsOf && <RoomUnitsDialog room={unitsOf} onClose={() => setUnitsOf(null)} />}
    </ListPage>
  );
}

const roomSchema = z.object({
  code: z.string(),
  name: zRequiredText(200),
  type: z.enum(ROOM_TYPES as [RoomType, ...RoomType[]], { error: 'common:validation.required' }),
  departmentId: z.string(),
  floor: z.string().max(16, { error: 'common:validation.too_long' }),
  tagCodes: z.array(z.string()).max(20),
  isBookable: z.boolean(),
  // ظرفیت گروهی (US-ORG-013؛ BR-ORG-013): ۱ تا ۵۰ نفر همزمان.
  capacity: z.string().refine((value) => /^\d{1,2}$/.test(latinDigits(value).trim()) && Number(latinDigits(value)) >= 1 && Number(latinDigits(value)) <= 50, { error: 'org:rooms.form.capacity_invalid' }),
});
type RoomForm = z.input<typeof roomSchema>;

/** انتخاب چندگانه برچسب‌ها (کد) با نام نمایشی. */
function TagsField({ control, tags }: { control: Control<RoomForm>; tags: readonly ResourceTagView[] }) {
  const { t } = useTranslation('org');
  const tagLabel = useTagLabel();
  const { field, fieldState } = useController({ name: 'tagCodes', control });
  const labelId = useId();
  const value = field.value ?? [];
  return (
    <TextField
      select
      fullWidth
      label={t('org:rooms.form.tags')}
      value={value}
      onChange={(event) => {
        const next = event.target.value as unknown;
        field.onChange(typeof next === 'string' ? next.split(',') : (next as string[]));
      }}
      onBlur={field.onBlur}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
      id={labelId}
      slotProps={{
        select: {
          multiple: true,
          renderValue: (selected) => (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(selected as string[]).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  label={tagLabel(
                    tags.find((tag) => tag.code === code),
                    code,
                  )}
                />
              ))}
            </Stack>
          ),
        },
        htmlInput: { 'data-testid': 'room-tags' },
      }}
    >
      {tags.map((tag) => (
        <MenuItem key={tag.code} value={tag.code} data-testid={`room-tag-option-${tag.code}`}>
          <Checkbox checked={value.includes(tag.code)} size="small" />
          <ListItemText primary={tagLabel(tag)} secondary={tag.code} />
        </MenuItem>
      ))}
    </TextField>
  );
}

function RoomFormDialog({
  room,
  branchId,
  branchName,
  departments,
  tags,
  onClose,
}: {
  /** null ← ایجاد. */
  room: RoomView | null;
  branchId: string;
  branchName: string;
  departments: readonly DepartmentView[];
  tags: readonly ResourceTagView[];
  onClose: () => void;
}) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = room === null;

  const schema = useMemo(
    () =>
      roomSchema.superRefine((value, ctx) => {
        if (creating && !ORG_CODE_PATTERN.test(value.code.trim())) {
          ctx.addIssue({ code: 'custom', path: ['code'], message: 'errors:validation.org_code' });
        }
      }),
    [creating],
  );
  const { control, handleSubmit, formState, setError } = useForm<RoomForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: room?.code ?? '',
      name: room?.name ?? '',
      type: room?.type,
      departmentId: room?.departmentId ?? '',
      floor: room?.floor ?? '',
      tagCodes: [...(room?.tagCodes ?? [])],
      isBookable: room?.isBookable ?? true,
      capacity: String(room?.capacity ?? 1),
    },
  });

  const save = useMutation({
    mutationFn: (form: RoomForm) => {
      const common = {
        departmentId: form.departmentId || null,
        name: form.name,
        floor: form.floor.trim() || null,
        type: form.type as RoomType,
        isBookable: form.isBookable,
        capacity: Number(latinDigits(form.capacity)),
        tagCodes: form.tagCodes,
      };
      return creating
        ? unwrap(
            tenantApi.POST('/api/v1/organization/rooms', {
              body: { ...common, branchId, code: form.code.trim() },
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/organization/rooms/{id}', {
              params: { path: { id: room.id } },
              body: { ...common, branchId: room.branchId, rowVersion: room.rowVersion },
            }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.rooms(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: orgKeys.tags(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'type', 'departmentId', 'floor', 'tagCodes', 'isBookable', 'capacity'],
        aliases: { department_id: 'departmentId', tag_codes: 'tagCodes', is_bookable: 'isBookable' },
        codeFields: { 'PHX-ORG-0201': 'code', 'PHX-ORG-0205': 'departmentId', 'PHX-ORG-0405': 'tagCodes' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-931-FORM"
      titleKey={creating ? 'org:rooms.create_title' : 'org:rooms.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      {!creating && (
        <Typography variant="caption" color="text.secondary">
          {t('org:rooms.form.branch_immutable', { branch: branchName })}
        </Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="org:rooms.form.code"
          helperKey={creating ? 'org:rooms.form.code_help' : 'org:rooms.form.code_immutable'}
          required
          fullWidth
          disabled={!creating}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'room-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="org:rooms.form.name"
          required
          fullWidth
          autoFocus
          slotProps={{ htmlInput: { 'data-testid': 'room-name' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="type"
          labelKey="org:rooms.form.type"
          required
          fullWidth
          options={ROOM_TYPES.map((value) => ({ value, labelKey: `org:room_type.${value}` }))}
        />
        <TextFieldRHF
          control={control}
          name="floor"
          labelKey="org:rooms.form.floor"
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'room-floor' } }}
        />
      </Stack>
      <SelectRHF
        control={control}
        name="departmentId"
        labelKey="org:rooms.form.department"
        fullWidth
        options={[
          { value: '', labelKey: 'org:rooms.form.department_none' },
          ...departments
            .filter((department) => department.isActive || department.id === room?.departmentId)
            .map((department) => ({ value: department.id, label: department.name })),
        ]}
      />
      <TagsField control={control} tags={tags} />
      <TextFieldRHF
        control={control}
        name="capacity"
        labelKey="org:rooms.form.capacity"
        helperKey="org:rooms.form.capacity_help"
        fullWidth
        slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'room-capacity' } }}
      />
      <CheckboxRHF
        control={control}
        name="isBookable"
        labelKey="org:rooms.form.is_bookable"
        helperKey="org:rooms.form.is_bookable_help"
      />
    </FormDialog>
  );
}

/** پیش‌نمایش اثر و تأیید غیرفعال‌سازی (AC-ORG-014.1، 014.2). */
function DeactivateRoomDialog({ room, onClose }: { room: RoomView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();

  const impact = useQuery({
    queryKey: ['organization', 'rooms', room.id, 'deactivation-impact'],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/rooms/{id}/deactivation-impact', {
          params: { path: { id: room.id } },
        }),
      ),
    gcTime: 0,
  });

  const deactivate = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/organization/rooms/{id}:deactivate', {
          params: { path: { id: room.id } },
          body: { confirm: true },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.rooms(session?.tenantId) });
      onClose();
    },
  });

  const data = impact.data;
  const blocked = data ? !data.canDeactivate : true;

  return (
    <Dialog
      open
      onClose={deactivate.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id={titleId}>{t('org:rooms.deactivate_dialog.title', { name: room.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} data-testid="room-deactivation-impact">
          {impact.isPending && (
            <>
              <Typography variant="body2">{t('org:rooms.deactivate_dialog.loading')}</Typography>
              <LoadingState rows={1} />
            </>
          )}
          {impact.error ? (
            <ErrorState error={impact.error} compact onRetry={() => void impact.refetch()} />
          ) : null}
          {data && Number(data.deployedDevices) > 0 && (
            <Alert severity="error" data-testid="room-impact-devices">
              {t('org:rooms.deactivate_dialog.devices', { n: faDigits(Number(data.deployedDevices)) })}
            </Alert>
          )}
          {data && Number(data.deployedDevices) === 0 && (
            <Alert
              severity={Number(data.futureBookings) > 0 ? 'warning' : 'info'}
              data-testid="room-impact-bookings"
              data-count={data.futureBookings}
            >
              {Number(data.futureBookings) > 0
                ? t('org:rooms.deactivate_dialog.bookings', { n: faDigits(Number(data.futureBookings)) })
                : t('org:rooms.deactivate_dialog.no_impact')}
            </Alert>
          )}
          {deactivate.error ? <ErrorState error={deactivate.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deactivate.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => deactivate.mutate()}
          disabled={blocked || deactivate.isPending}
          data-testid="room-deactivate-confirm"
        >
          {t('org:rooms.deactivate_dialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
